// =============================================================================
// src/lib/signalling.ts  –  WebSocket signalling client
//   • Auto-reconnect with exponential backoff
//   • Typed message routing via event listeners
//   • Keepalive ping every 25 s
// =============================================================================

import { logDebug } from './debug';

export type WsMsgType =
  | 'SDP_OFFER' | 'SDP_ANSWER' | 'ICE_CANDIDATE'
  | 'PEER_JOINED' | 'PEER_LEFT' | 'ROOM_INFO' | 'ERROR'
  | 'FILE_MANIFEST'
  | 'TRANSFER_ACCEPT' | 'TRANSFER_DECLINE'
  | 'CHUNK_ACK' | 'RESUME' | 'PROGRESS'
  | 'TRANSFER_COMPLETE' | 'TRANSFER_CANCEL' | 'TRANSFER_FAILED'
  | 'TRANSFER_PAUSE' | 'TRANSFER_RESUME'
  | 'CHAT_MESSAGE' | 'CHAT_HISTORY' | 'TYPING_START' | 'TYPING_STOP' | 'READ_RECEIPT'
  | 'PING' | 'PONG'
  | 'DROP_MODERATION';

export interface WsMsg {
  type:    WsMsgType;
  from?:   string;
  to?:     string;
  ts:      number;
  payload: unknown;
}

export type MsgHandler = (msg: WsMsg) => void;

const PING_INTERVAL_MS   = 25_000;
const MAX_RECONNECT_MS   = 30_000;
const INITIAL_BACKOFF_MS = 1_000;

export class SignallingClient {
  private ws:         WebSocket | null = null;
  private wsUrl:      string = '';
  private handlers    = new Map<WsMsgType, Set<MsgHandler>>();
  private anyHandlers = new Set<MsgHandler>();
  private pingTimer:  ReturnType<typeof setInterval> | null = null;
  private reconnTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = INITIAL_BACKOFF_MS;
  private intentionalClose = false;
  private _connected = false;

  onOpen?:  () => void;
  onClose?: (code: number, reason: string) => void;

  get connected(): boolean { return this._connected; }

  // Returns true while a WebSocket connection attempt is in progress (CONNECTING).
  // Used by the LAN poll to avoid interrupting an in-flight connection.
  get connecting(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.CONNECTING;
  }

  connect(wsUrl: string): void {
    this.wsUrl = wsUrl;
    this.intentionalClose = false;
    logDebug('ws', 'connect.requested', { wsUrl });
    this._open();
  }

  private _open(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
    }

    this.ws = new WebSocket(this.wsUrl);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this._connected = true;
      this.backoff = INITIAL_BACKOFF_MS;
      this._startPing();
      logDebug('ws', 'connect.open', { wsUrl: this.wsUrl });
      this.onOpen?.();
    };

    this.ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      try {
        const msg = JSON.parse(ev.data) as WsMsg;
        if (msg.type === 'PONG') return;
        logDebug('ws', 'message.in', { type: msg.type, from: msg.from, to: msg.to });
        this.anyHandlers.forEach(h => h(msg));
        this.handlers.get(msg.type)?.forEach(h => h(msg));
      } catch { /* malformed */ }
    };

    this.ws.onerror = () => {
      logDebug('ws', 'connect.error', { wsUrl: this.wsUrl }, 'warn');
      /* onclose fires next */
    };

    this.ws.onclose = (ev) => {
      this._connected = false;
      this._stopPing();
      logDebug('ws', 'connect.close', {
        wsUrl:           this.wsUrl,
        intentionalClose: this.intentionalClose,
        code:            ev.code,
        reason:          ev.reason || '(none)',
        wasClean:        ev.wasClean,
      }, this.intentionalClose ? 'info' : 'warn');
      this.onClose?.(ev.code, ev.reason);
      if (!this.intentionalClose) this._scheduleReconnect();
    };
  }

  private _startPing(): void {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'PING', ts: Date.now(), payload: {} });
    }, PING_INTERVAL_MS);
  }

  private _stopPing(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private _scheduleReconnect(): void {
    if (this.reconnTimer) clearTimeout(this.reconnTimer);
    logDebug('ws', 'reconnect.scheduled', { backoffMs: this.backoff });
    this.reconnTimer = setTimeout(() => {
      this.backoff = Math.min(this.backoff * 2, MAX_RECONNECT_MS);
      logDebug('ws', 'reconnect.attempt', { nextBackoffMs: this.backoff });
      this._open();
    }, this.backoff);
  }

  send(msg: Omit<WsMsg, 'ts'> & { ts?: number }): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      logDebug('ws', 'message.out', { type: msg.type, to: msg.to });
      this.ws.send(JSON.stringify({ ts: Date.now(), ...msg }));
    } else {
      logDebug('ws', 'message.out.dropped', {
        type: msg.type,
        to: msg.to,
        state: this.ws?.readyState ?? 'null',
      }, 'warn');
    }
  }

  on(type: WsMsgType, handler: MsgHandler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(handler);
    return () => this.handlers.get(type)?.delete(handler);
  }

  onAny(handler: MsgHandler): () => void {
    this.anyHandlers.add(handler);
    return () => this.anyHandlers.delete(handler);
  }

  disconnect(): void {
    this.intentionalClose = true;
    this._stopPing();
    if (this.reconnTimer) { clearTimeout(this.reconnTimer); this.reconnTimer = null; }
    logDebug('ws', 'disconnect.called', { wsUrl: this.wsUrl });
    this.ws?.close(1000, 'Intentional disconnect');
    this._connected = false;
  }
}
